import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';

import type { CustomerGroupRequest, CustomerGroupResponse } from '../../model/customerGroupTypes';

const CODE_PATTERN = /^[A-Za-z0-9-]+$/;

export interface CustomerGroupFormDialogProps {
  open: boolean;
  /** Present = edit (code locked); absent = create. Seeded from the row already held by the list
   * query — there is no `GET .../customer-groups/{code}` on the live BFF contract. */
  group?: CustomerGroupResponse;
  pending?: boolean;
  onClose: () => void;
  onSave: (body: CustomerGroupRequest) => void;
}

/** Create / edit customer-group dialog (design spec §8.10). No `kind` selector: the write DTO
 * (`CustomerGroupRequest`) has no `kind` field at all on the live BFF contract, so every group
 * created here is implicitly STATIC — a served DYNAMIC group still edits name/priority through
 * this same dialog (its criteria, if any, are authored elsewhere), just never via a kind toggle. */
export function CustomerGroupFormDialog({
  open,
  group,
  pending = false,
  onClose,
  onSave,
}: CustomerGroupFormDialogProps) {
  const { t } = useTranslation('catalog');
  const isEdit = Boolean(group);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [priority, setPriority] = useState('0');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? '');
    setCode(group?.code ?? '');
    setPriority(group ? String(group.priority) : '0');
    setDescription(group?.description ?? '');
  }, [open, group]);

  const nameValid = name.trim() !== '';
  const codeValid = isEdit || (code.trim() !== '' && CODE_PATTERN.test(code.trim()));
  const valid = nameValid && codeValid;

  const handleSave = () => {
    const parsedPriority = Number.parseInt(priority, 10);
    onSave({
      code: isEdit ? (group as CustomerGroupResponse).code : code.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      priority: Number.isFinite(parsedPriority) ? parsedPriority : 0,
    });
  };

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? t('customerGroupsPanel.editTitle') : t('customerGroupsPanel.create')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label={t('customerGroupsPanel.nameLabel')}
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={name !== '' && !nameValid}
            fullWidth
            autoFocus
          />
          <Tooltip title={isEdit ? t('customerGroupsPanel.codeImmutable') : ''} disableHoverListener={!isEdit}>
            <span>
              <TextField
                label={t('customerGroupsPanel.codeLabel')}
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
                error={!isEdit && code !== '' && !codeValid}
                helperText={isEdit ? t('customerGroupsPanel.codeImmutable') : t('customerGroupsPanel.codeHelper')}
                disabled={isEdit}
                fullWidth
                slotProps={{ htmlInput: { style: { fontFamily: '"JetBrains Mono", monospace' } } }}
              />
            </span>
          </Tooltip>
          <TextField
            label={t('customerGroupsPanel.priorityLabel')}
            type="number"
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
            helperText={t('customerGroupsPanel.priorityHelper')}
            slotProps={{ htmlInput: { step: 1 } }}
          />
          <TextField
            label={t('customerGroupsPanel.descriptionLabel')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={handleSave}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
